import React, { useCallback, useEffect, useState } from 'react';
import { ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { API } from '../../config/apiBase';
import { useAuth } from '../../context/AuthContext';
import {
    ACOES, MARKETPLACES, MOTIVOS_FONTE, ROTA_API, STATUS, contadoresDoResumo, lerLote, lotesDoResumo, percentual,
    reais, resumoMotivos, urlOrigemSegura, validacaoExibida,
} from './coworkOfertas';

/**
 * /sistema/cowork-ofertas — Cowork Offer Hunter, Fase 1 (shadow).
 *
 * Somente admin (a API também exige). Mostra candidatos encontrados pelo Cowork, com o
 * resultado dos mesmos gates do Super Vitrine, filtráveis por lote. Ações: aprovar para
 * shadow, rejeitar, marcar revisão e abrir a página pública de origem. Não existe publicação
 * daqui. Validação (frescura, confiança do desconto, claim, variante, match) é exibida como
 * veio da API; nada é recalculado.
 *
 * Desktop: tabela. Celular: um card expansível por candidato.
 * Não renderiza DealCard nem imagens externas: esta página não gera exposição, clique ou
 * evento de oferta.
 */
const token = () => localStorage.getItem('token') || sessionStorage.getItem('token');

async function chamar(caminho, opcoes = {}) {
    const r = await fetch(`${API}${ROTA_API}${caminho}`, {
        ...opcoes,
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
    });
    let corpo = null;
    try { corpo = await r.json(); } catch (_) { corpo = null; }
    if (!r.ok) throw new Error((corpo && corpo.detail) || `HTTP ${r.status}`);
    return corpo;
}

const campo = { background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' };
const suave = { color: 'var(--muted)' };

function Cartao({ rotulo, valor, testid }) {
    return (
        <div className="rounded-lg px-3 py-2" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={testid}>
            <p className="text-[11px]" style={suave}>{rotulo}</p>
            <p className="text-lg font-bold" style={{ color: 'var(--text)' }}>{valor ?? 0}</p>
        </div>
    );
}

function Status({ valor }) {
    const st = STATUS[valor] || { label: valor, cls: 'sv-badge-muted' };
    return <span className={`sv-badge ${st.cls}`} data-testid="cowork-status">{st.label}</span>;
}

function Motivos({ item }) {
    const m = resumoMotivos(item);
    if (!m.extras && m.todos.length <= 1) return <span data-testid="cowork-motivo">{m.principal}</span>;
    return (
        <details data-testid="cowork-motivo">
            <summary className="cursor-pointer">{m.principal} <span style={suave}>+{m.extras}</span></summary>
            <ul className="mt-1 space-y-0.5">
                {m.todos.map((x) => <li key={x.codigo} title={x.codigo}>{x.rotulo}</li>)}
            </ul>
        </details>
    );
}

function Acoes({ item, onAcao }) {
    const origem = urlOrigemSegura(item.url_found);
    return (
        <div className="flex flex-wrap gap-1">
            {ACOES.map((a) => (
                <button key={a.status} type="button" className="sv-btn-system text-[11px]" style={{ padding: '2px 6px' }}
                    onClick={() => onAcao(item._id, a.status)} data-testid={`acao-${a.status}`}>{a.label}</button>
            ))}
            {origem && (
                <a href={origem} target="_blank" rel="noopener noreferrer nofollow" referrerPolicy="no-referrer"
                    className="sv-btn-system text-[11px] inline-flex items-center gap-1" style={{ padding: '2px 6px' }} data-testid="acao-origem">
                    <ExternalLink size={11} /> Origem
                </a>
            )}
        </div>
    );
}

function Detalhes({ item }) {
    const v = validacaoExibida(item);
    const linhas = [
        ['Marketplace', MARKETPLACES[item.marketplace] || item.marketplace],
        ['Preço anterior', reais(item.original_price_found)],
        ['Frescura', `${v.frescuraOrigem} · ${v.frescuraConfianca} · ${v.frescuraIdade}`],
        ['Confiança do desconto', v.confiancaDesconto],
        ['Claim risk', v.claim],
        ['Risco de variante', v.variante],
        ['Match', v.match],
        ['Busca', item.source_search_term || '—'],
        ['Encontrado em', item.observed_at ? new Date(item.observed_at).toLocaleString('pt-BR') : '—'],
        ['Lote', item.run_id || '—'],
    ];
    return (
        <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-[12px]" data-testid="cowork-detalhes">
            {linhas.map(([k, val]) => (
                <React.Fragment key={k}>
                    <dt style={suave}>{k}</dt>
                    <dd style={{ color: 'var(--text)', wordBreak: 'break-word' }}>{val}</dd>
                </React.Fragment>
            ))}
        </dl>
    );
}

export default function CoworkOfertasPage() {
    const { user } = useAuth();
    const [aba, setAba] = useState('candidatos');
    const [resumo, setResumo] = useState(null);
    const [lotes, setLotes] = useState([]);
    const [itens, setItens] = useState([]);
    const [filtro, setFiltro] = useState({ lote: '', status: '', marketplace: '' });
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState('');
    const [runId, setRunId] = useState('');
    const [termos, setTermos] = useState('');
    const [lote, setLote] = useState('');
    const [resultado, setResultado] = useState(null);
    const [fonte, setFonte] = useState({ marketplace: 'amazon', reason: 'captcha' });
    const [impacto, setImpacto] = useState(null);

    const admin = user?.role === 'admin';

    const carregar = useCallback(async () => {
        setCarregando(true);
        setErro('');
        try {
            const q = new URLSearchParams();
            if (filtro.lote) q.set('run_id', filtro.lote);
            if (filtro.status) q.set('status', filtro.status);
            if (filtro.marketplace) q.set('marketplace', filtro.marketplace);
            const doLote = filtro.lote ? `?run_id=${encodeURIComponent(filtro.lote)}` : '';
            const pedidos = [chamar(`/candidates?${q}`), chamar(`/summary${doLote}`)];
            if (filtro.lote) pedidos.push(chamar('/summary'));
            const [lista, sumario, geral] = await Promise.all(pedidos);
            setItens(lista.items || []);
            setResumo(sumario);
            setLotes(lotesDoResumo(geral || sumario));
            try { setImpacto(await chamar('/shadow')); } catch (_) { setImpacto(null); }
        } catch (e) {
            setErro(e.message);
        } finally {
            setCarregando(false);
        }
    }, [filtro]);

    useEffect(() => { if (admin) carregar(); }, [admin, carregar]);

    if (!admin) {
        return (
            <div className="sv-page" data-testid="cowork-sem-acesso">
                <p style={suave}>Acesso restrito a administradores.</p>
            </div>
        );
    }

    const acao = async (id, status) => {
        try {
            await chamar(`/candidates/${encodeURIComponent(id)}/status`, { method: 'PATCH', body: JSON.stringify({ status }) });
            carregar();
        } catch (e) {
            setErro(`${id}: ${e.message}`);
        }
    };

    const abrirLote = async () => {
        const planned = termos.split('\n').map((t) => t.trim()).filter(Boolean).slice(0, 30);
        try {
            const r = await chamar('/runs', { method: 'POST', body: JSON.stringify({ planned_terms: planned }) });
            setRunId(r.run_id);
            carregar();
        } catch (e) { setErro(e.message); }
    };

    const enviarLote = async () => {
        const lido = lerLote(lote);
        if (lido.erro) { setErro(lido.erro); return; }
        try {
            const r = await chamar('/candidates', { method: 'POST', body: JSON.stringify({ run_id: runId, candidates: lido.candidatos }) });
            setResultado(r.results);
            carregar();
        } catch (e) { setErro(e.message); }
    };

    const registrarFonte = async (status) => {
        const corpo = { marketplace: fonte.marketplace, status, reason: status === 'source_unavailable' ? fonte.reason : null };
        try {
            await chamar(`/runs/${encodeURIComponent(runId)}/sources`, { method: 'POST', body: JSON.stringify(corpo) });
            setResultado([{ fonte: fonte.marketplace, status, reason: corpo.reason }]);
            carregar();
        } catch (e) { setErro(e.message); }
    };

    const encerrar = async () => {
        try { await chamar(`/runs/${encodeURIComponent(runId)}/close`, { method: 'POST' }); setRunId(''); carregar(); } catch (e) { setErro(e.message); }
    };

    const cont = contadoresDoResumo(resumo);

    return (
        <div className="sv-page" data-testid="cowork-ofertas-page">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>Cowork Ofertas</h2>
                <span className="text-xs px-2 py-1 rounded" style={{ background: 'var(--surface-2)', color: 'var(--muted)' }} data-testid="cowork-modo">
                    Shadow mode · auto_publish: false · nada daqui é publicado
                </span>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-3">
                <label className="text-xs" style={suave} htmlFor="cowork-filtro-lote">Lote</label>
                <select id="cowork-filtro-lote" value={filtro.lote} onChange={(e) => setFiltro({ ...filtro, lote: e.target.value })}
                    className="text-xs px-2 py-1.5 rounded-lg max-w-full" style={campo} data-testid="cowork-filtro-lote">
                    <option value="">Todos os lotes</option>
                    {lotes.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                {filtro.lote && <span className="text-[11px]" style={suave} data-testid="cowork-escopo">Contadores e relatório deste lote</span>}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-4" data-testid="cowork-contadores">
                <Cartao rotulo="Encontrados" valor={cont.encontrados} testid="cowork-encontrados" />
                <Cartao rotulo="Aprovado (shadow)" valor={cont.approved_shadow} testid="cowork-approved_shadow" />
                <Cartao rotulo="Rejeitado" valor={cont.rejected} testid="cowork-rejected" />
                <Cartao rotulo="Duplicado" valor={cont.duplicate} testid="cowork-duplicate" />
                <Cartao rotulo="Revisar" valor={cont.needs_review} testid="cowork-needs_review" />
                <Cartao rotulo="Expirado" valor={cont.expired} testid="cowork-expired" />
                <Cartao rotulo="Novos p/ crawler" valor={cont.novos} testid="cowork-novos" />
                <Cartao rotulo="Já conhecidos" valor={cont.conhecidos} testid="cowork-conhecidos" />
            </div>

            <div className="flex flex-wrap gap-2 mb-3" role="tablist">
                {[['candidatos', 'Candidatos'], ['ingestao', 'Ingestão'], ['relatorio', 'Relatório'], ['impacto', 'Impacto Shadow']].map(([k, rotulo]) => (
                    <button key={k} type="button" role="tab" aria-selected={aba === k} className="sv-btn-system text-xs"
                        style={aba === k ? { borderColor: 'var(--sv-gold-primary)' } : undefined} onClick={() => setAba(k)} data-testid={`aba-${k}`}>
                        {rotulo}
                    </button>
                ))}
                <button type="button" className="sv-btn-system text-xs ml-auto" onClick={carregar} data-testid="cowork-atualizar">
                    <RefreshCw size={12} /> Atualizar
                </button>
            </div>

            {erro && <p className="text-xs mb-2" style={{ color: '#ef4444' }} data-testid="cowork-erro">{erro}</p>}

            {aba === 'candidatos' && (
                <>
                    <div className="flex flex-wrap gap-2 mb-3">
                        <select value={filtro.status} onChange={(e) => setFiltro({ ...filtro, status: e.target.value })} className="text-xs px-2 py-1.5 rounded-lg" style={campo} data-testid="cowork-filtro-status">
                            <option value="">Todos os status</option>
                            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                        <select value={filtro.marketplace} onChange={(e) => setFiltro({ ...filtro, marketplace: e.target.value })} className="text-xs px-2 py-1.5 rounded-lg" style={campo} data-testid="cowork-filtro-marketplace">
                            <option value="">Todos os marketplaces</option>
                            {Object.entries(MARKETPLACES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                    </div>
                    {carregando ? (
                        <div className="flex justify-center py-10"><Loader2 size={22} className="animate-spin" /></div>
                    ) : (
                        <>
                            <div className="hidden md:block" style={{ overflowX: 'auto' }} data-testid="cowork-desktop">
                                <table className="w-full text-xs" data-testid="cowork-tabela">
                                    <thead>
                                        <tr style={{ color: 'var(--muted)', textAlign: 'left' }}>
                                            {['Título', 'Marketplace', 'Preço', 'Preço anterior', 'Desconto', 'Score', 'Status', 'Frescura', 'Conf. desconto', 'Claim', 'Variante', 'Match', 'Motivo', 'Ações'].map((c) => (
                                                <th key={c} className="px-2 py-1.5 font-semibold whitespace-nowrap">{c}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {itens.map((c) => {
                                            const v = validacaoExibida(c);
                                            return (
                                                <tr key={c._id} style={{ borderTop: '1px solid var(--border)', color: 'var(--text)', verticalAlign: 'top' }} data-testid={`cowork-linha-${c._id}`}>
                                                    <td className="px-2 py-1.5" style={{ minWidth: 220, maxWidth: 320 }}>
                                                        {c.validation?.display_title || c.title_raw}
                                                        <div className="text-[11px]" style={suave}>{c.source_search_term || '—'} · {c.observed_at ? new Date(c.observed_at).toLocaleString('pt-BR') : '—'}</div>
                                                    </td>
                                                    <td className="px-2 py-1.5 whitespace-nowrap">{MARKETPLACES[c.marketplace] || c.marketplace}</td>
                                                    <td className="px-2 py-1.5 whitespace-nowrap">{reais(c.price_found)}</td>
                                                    <td className="px-2 py-1.5 whitespace-nowrap">{reais(c.original_price_found)}</td>
                                                    <td className="px-2 py-1.5">{percentual(c.discount_found)}</td>
                                                    <td className="px-2 py-1.5">{typeof c.validation_score === 'number' ? c.validation_score.toFixed(1) : '—'}</td>
                                                    <td className="px-2 py-1.5"><Status valor={c.validation_status} /></td>
                                                    <td className="px-2 py-1.5" data-testid="col-frescura">
                                                        <div>{v.frescuraOrigem}</div>
                                                        <div className="text-[11px]" style={suave}>{v.frescuraConfianca} · {v.frescuraIdade}</div>
                                                    </td>
                                                    <td className="px-2 py-1.5" data-testid="col-conf-desconto">{v.confiancaDesconto}</td>
                                                    <td className="px-2 py-1.5" data-testid="col-claim">{v.claim}</td>
                                                    <td className="px-2 py-1.5" data-testid="col-variante">{v.variante}</td>
                                                    <td className="px-2 py-1.5" data-testid="col-match">{v.match}</td>
                                                    <td className="px-2 py-1.5" style={{ ...suave, minWidth: 180 }}><Motivos item={c} /></td>
                                                    <td className="px-2 py-1.5" style={{ minWidth: 150 }}><Acoes item={c} onAcao={acao} /></td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <ul className="md:hidden grid gap-2" data-testid="cowork-mobile">
                                {itens.map((c) => (
                                    <li key={c._id} className="rounded-lg" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`cowork-card-${c._id}`}>
                                        <details>
                                            <summary className="p-3 cursor-pointer list-none">
                                                <p className="text-[13px] font-semibold" style={{ color: 'var(--text)' }}>{c.validation?.display_title || c.title_raw}</p>
                                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[12px]" style={{ color: 'var(--text)' }}>
                                                    <span>{MARKETPLACES[c.marketplace] || c.marketplace}</span>
                                                    <span>{reais(c.price_found)}</span>
                                                    <span>{percentual(c.discount_found)}</span>
                                                    <span>Score {typeof c.validation_score === 'number' ? c.validation_score.toFixed(1) : '—'}</span>
                                                    <Status valor={c.validation_status} />
                                                </div>
                                            </summary>
                                            <div className="px-3 pb-3 grid gap-2">
                                                <Detalhes item={c} />
                                                <div className="text-[12px]" style={suave}><Motivos item={c} /></div>
                                                <Acoes item={c} onAcao={acao} />
                                            </div>
                                        </details>
                                    </li>
                                ))}
                            </ul>
                            {itens.length === 0 && <p className="text-xs py-6 text-center" style={suave}>Nenhum candidato.</p>}
                        </>
                    )}
                </>
            )}

            {aba === 'ingestao' && (
                <div className="grid gap-3" data-testid="cowork-ingestao">
                    <p className="text-xs" style={suave}>
                        Perfil sem login nos marketplaces. Só dados visíveis; campo ausente = null. Captcha, login, challenge ou
                        bm-verify: registrar fonte indisponível e seguir. Nunca comprar, aplicar cupom, clicar CTA comercial ou abrir o redirector.
                    </p>
                    {!runId ? (
                        <div className="grid gap-2">
                            <textarea value={termos} onChange={(e) => setTermos(e.target.value)} rows={4} placeholder="Termos planejados, um por linha" className="text-xs p-2 rounded-lg" style={campo} data-testid="cowork-termos" />
                            <button type="button" className="sv-btn-gold-system sv-btn-system text-xs" onClick={abrirLote} data-testid="cowork-abrir-lote">Abrir lote</button>
                        </div>
                    ) : (
                        <>
                            <p className="text-xs" style={{ color: 'var(--text)' }} data-testid="cowork-run">Lote: {runId}</p>
                            <div className="flex flex-wrap gap-2 items-center rounded-lg p-2" style={{ border: '1px solid var(--border)' }} data-testid="cowork-fonte">
                                <span className="text-xs" style={suave}>Status da fonte</span>
                                <select value={fonte.marketplace} onChange={(e) => setFonte({ ...fonte, marketplace: e.target.value })} className="text-xs px-2 py-1.5 rounded-lg" style={campo} data-testid="cowork-fonte-marketplace">
                                    {Object.entries(MARKETPLACES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                </select>
                                <button type="button" className="sv-btn-system text-xs" onClick={() => registrarFonte('ok')} data-testid="cowork-fonte-ok">Fonte ok</button>
                                <select value={fonte.reason} onChange={(e) => setFonte({ ...fonte, reason: e.target.value })} className="text-xs px-2 py-1.5 rounded-lg" style={campo} data-testid="cowork-fonte-motivo">
                                    {MOTIVOS_FONTE.map((m) => <option key={m} value={m}>{m}</option>)}
                                </select>
                                <button type="button" className="sv-btn-system text-xs" onClick={() => registrarFonte('source_unavailable')} data-testid="cowork-fonte-indisponivel">Fonte indisponível</button>
                            </div>
                            <textarea value={lote} onChange={(e) => setLote(e.target.value)} rows={10} placeholder='[{"marketplace":"mercadolivre","url_found":"https://...","title_raw":"...","price_found":199.9,"original_price_found":null,"discount_found":null,"observed_at":"2026-09-18T12:00:00Z"}]'
                                className="text-xs p-2 rounded-lg font-mono" style={campo} data-testid="cowork-json" />
                            <div className="flex flex-wrap gap-2 items-center">
                                <button type="button" className="sv-btn-gold-system sv-btn-system text-xs" onClick={enviarLote} data-testid="cowork-enviar">Enviar candidatos</button>
                                <button type="button" className="sv-btn-system text-xs ml-auto" onClick={encerrar} data-testid="cowork-encerrar">Encerrar lote</button>
                            </div>
                        </>
                    )}
                    {resultado && <pre className="text-[11px] p-2 rounded-lg overflow-auto" style={campo} data-testid="cowork-resultado">{JSON.stringify(resultado, null, 1)}</pre>}
                </div>
            )}

            {aba === 'impacto' && (
                <div className="grid gap-3 text-xs" style={{ color: 'var(--text)' }} data-testid="cowork-impacto">
                    <p style={suave} data-testid="cowork-impacto-flag">
                        Comparação shadow {impacto?.enabled ? 'habilitada' : 'desligada'} em produção · auto_publish: false ·
                        diagnóstico apenas: candidato Cowork não publica
                    </p>
                    {!impacto?.resumo ? (
                        <p style={suave} data-testid="cowork-impacto-vazio">Nenhuma análise shadow gravada ainda.</p>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="cowork-impacto-numeros">
                                <Cartao rotulo="Slots analisados" valor={impacto.resumo.slots} testid="impacto-slots" />
                                <Cartao rotulo="Candidatos avaliados" valor={impacto.resumo.cowork_avaliados} testid="impacto-avaliados" />
                                <Cartao rotulo="Elegíveis" valor={impacto.resumo.cowork_elegiveis} testid="impacto-elegiveis" />
                                <Cartao rotulo="Barrados na revalidação" valor={impacto.resumo.cowork_rejeitados_na_revalidacao} testid="impacto-barrados" />
                                <Cartao rotulo="Empates técnicos" valor={impacto.resumo.technical_ties ?? 0} testid="impacto-empates" />
                                <Cartao rotulo="Decisivas Cowork" valor={impacto.resumo.cowork_decisive_wins ?? 0} testid="impacto-decisivas-cowork" />
                                <Cartao rotulo="Decisivas normal" valor={impacto.resumo.normal_decisive_wins ?? 0} testid="impacto-decisivas-normal" />
                                <Cartao rotulo="Score médio" valor={impacto.resumo.score_medio_cowork ?? '—'} testid="impacto-score-medio" />
                            </div>
                            <p style={suave} data-testid="impacto-bruto">
                                Modo {impacto.resumo.analysis_mode || '—'} (não é histórico exato) · empate técnico abaixo de{' '}
                                {impacto.resumo.technical_tie_threshold ?? 1} ponto · bruto, só auditoria: Cowork à frente em{' '}
                                {impacto.resumo.cowork_raw_wins ?? impacto.resumo.cowork_venceria ?? 0} slot(s), normal em{' '}
                                {impacto.resumo.normal_raw_wins ?? impacto.resumo.normal_venceria ?? 0}
                            </p>
                            <div>
                                <p className="font-semibold mb-1">Motivos de não elegibilidade</p>
                                <ul data-testid="cowork-impacto-motivos">
                                    {Object.entries(impacto.resumo.motivos || {}).map(([m, n]) => (
                                        <li key={m} title={m}>{resumoMotivos({ rejection_reasons: [m] }).principal}: {n}</li>
                                    ))}
                                </ul>
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                                <table className="w-full text-xs" data-testid="cowork-impacto-tabela">
                                    <thead>
                                        <tr style={{ color: 'var(--muted)', textAlign: 'left' }}>
                                            {['Slot', 'Vencedor normal', 'Score normal', 'Melhor Cowork', 'Score Cowork', 'Delta', 'Resultado'].map((c) => (
                                                <th key={c} className="px-2 py-1.5 font-semibold whitespace-nowrap">{c}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(impacto.slots || []).map((s) => (
                                            <tr key={s.slot_id} style={{ borderTop: '1px solid var(--border)' }} data-testid={`impacto-linha-${s.slot_id}`}>
                                                <td className="px-2 py-1.5">{s.slot_id}</td>
                                                <td className="px-2 py-1.5">{s.normal_winner?.display_title || s.normal_winner?.deal_id || '—'}</td>
                                                <td className="px-2 py-1.5">{s.normal_winner ? s.normal_winner.score.toFixed(1) : '—'}</td>
                                                <td className="px-2 py-1.5">{s.cowork_best?.display_title || s.cowork_best?.candidate_id || '—'}</td>
                                                <td className="px-2 py-1.5">{s.cowork_best ? s.cowork_best.score.toFixed(1) : '—'}</td>
                                                <td className="px-2 py-1.5">{typeof s.score_delta === 'number' ? s.score_delta.toFixed(1) : '—'}</td>
                                                <td className="px-2 py-1.5" data-testid="impacto-resultado">
                                                    {s.decisive_winner === 'cowork' ? 'Cowork (decisiva)'
                                                        : s.decisive_winner === 'normal' ? 'Normal (decisiva)'
                                                            : s.decisive_winner === 'tie' ? 'Empate técnico' : 'Sem comparação'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            )}

            {aba === 'relatorio' && resumo && (
                <div className="grid gap-3 text-xs" style={{ color: 'var(--text)' }} data-testid="cowork-relatorio">
                    <p style={suave}>{filtro.lote ? `Lote ${filtro.lote}` : 'Todos os lotes'}</p>
                    <div>
                        <p className="font-semibold mb-1">Comparação com o crawler</p>
                        <p>Já conhecidos: {cont.conhecidos} · Match fraco por título: {cont.fracos} · Novos: {cont.novos} · Novos aprovados (shadow): {resumo.crawler_comparison?.new_approved_shadow ?? 0}</p>
                    </div>
                    <div>
                        <p className="font-semibold mb-1">Motivos</p>
                        <ul>{Object.entries(resumo.reasons || {}).map(([m, n]) => <li key={m} title={m}>{resumoMotivos({ rejection_reasons: [m] }).principal}: {n}</li>)}</ul>
                    </div>
                    <div>
                        <p className="font-semibold mb-1">Por marketplace</p>
                        <ul>{Object.entries(resumo.by_marketplace || {}).map(([m, v]) => <li key={m}>{MARKETPLACES[m] || m}: {v.found} encontrados, {v.approved_shadow || 0} aprovados (shadow), {v.duplicate || 0} duplicados, {v.rejected || 0} rejeitados</li>)}</ul>
                    </div>
                    <div>
                        <p className="font-semibold mb-1">Fontes</p>
                        <ul>{(resumo.sources || []).flatMap((l) => (l.sources || []).map((s, i) => <li key={`${l.run_id}-${i}`}>{l.run_id}: {MARKETPLACES[s.marketplace] || s.marketplace} {s.status}{s.reason ? ` (${s.reason})` : ''}</li>))}</ul>
                    </div>
                </div>
            )}
        </div>
    );
}
